import { useEffect, useMemo, useState } from 'react';

import { getVersionedKey, local } from '../utils/localStorage';
import type { Options } from '../utils/localStorage';

export function useGetLocalStorage<T>(
    key: string | null | undefined,
    initial?: T,
    { version = undefined, listenForUpdates }: Options = {},
) {
    const [refetchStorageValue, setRefetchStorageValue] = useState(false);
    const [shouldPreventRefetch, setShouldPreventRefetch] = useState(false);

    useEffect(() => {
        if (listenForUpdates) {
            const handler = () => {
                setRefetchStorageValue(true);
            };
            window.addEventListener(`local_storage_updated_${key}`, handler);
            return () => {
                window.removeEventListener(
                    `local_storage_updated_${key}`,
                    handler,
                );
            };
        }
    }, []);

    return useMemo(() => {
        if (shouldPreventRefetch) {
            return setShouldPreventRefetch(false);
        }
        if (!key) {
            return initial;
        }

        const value = local.getItem(getVersionedKey(key, version));

        if (refetchStorageValue) {
            setRefetchStorageValue(false);
            setShouldPreventRefetch(true);
        }

        if (value === undefined || value === null) {
            return initial;
        }
        return value as T;
    }, [initial, key, version, refetchStorageValue]);
}
